import React, { useEffect, useRef, useState } from 'react';

import { ActionIcon, Box, Button, Divider, Flex, Textarea, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Document, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import { IconCheck, IconDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import weekday from 'dayjs/plugin/weekday';
import Markdown from 'markdown-to-jsx';

import { useUpdatePatientMutation } from '@/store/slices/api/patientsApiSlice';
import { appointmentTypeKey } from '@/utils/appointmentFrequency';
import formatDate from '@/utils/formatDate';
import InfoMessage from '@/components/InfoMessage';

import { GetPatientSchedulesResponse, Patient } from '@/types/patient';
import CustomModal from '@/ui/Modal';
import TextWrapper from '@/ui/TextWrapper';

import { renderMarkdownToPdf } from './markdownToPdf';
import { extractBlockAlignment, splitMarkdownIntoBlocks } from './printMarkdown';
import {
  preserveLineBreaksForMarkdown,
  PrintTextVariables,
  substitutePrintTextVariables
} from './printTextVariables';

dayjs.extend(weekday);
dayjs.extend(localizedFormat);

interface PrintScheduleIndexProps {
  patient: Patient;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  printData: GetPatientSchedulesResponse | undefined;
  isLoading?: boolean;
}

const pdfStyles = StyleSheet.create({
  page: {
    padding: '30px 60px',
    fontFamily: 'Helvetica',
    fontSize: 12,
    color: '#213147'
  },
  bodyParagraph: {
    marginBottom: 10,
    lineHeight: 1.4
  },
  bodyHeading: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 14
  },
  bodyListItem: {
    marginBottom: 4
  },
  bodyBold: {
    fontFamily: 'Helvetica-Bold'
  },
  bodyItalic: {
    fontFamily: 'Helvetica-Oblique'
  },
  bodyDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#CCCCCC',
    marginTop: 10,
    marginBottom: 10
  },
  noteBody: {
    fontSize: 11,
    lineHeight: 1.4,
    marginTop: 20
  }
});

const pdfMarkdownStyles = {
  paragraph: pdfStyles.bodyParagraph,
  heading: pdfStyles.bodyHeading,
  listItem: pdfStyles.bodyListItem,
  bold: pdfStyles.bodyBold,
  italic: pdfStyles.bodyItalic,
  divider: pdfStyles.bodyDivider
};

// The letter is entirely markdown-driven — no hardcoded header, salutation, schedule list, or
// sign-off. An org's configured print text (see operations-portal) is expected to include
// whatever fixed text it wants, using ${patientName}/${patientAddress}/${clinicName}/
// ${currentDate}/${nextScheduleDate} to personalize it — see PrintScheduleIndex's `vars` below.
// The same text is shown to every patient in the org; there is no per-patient override anymore.
// `note`, if present, is a physician's free-text annotation appended below the letter body.
const ScheduleLetterPdfDocument: React.FC<{ bodyMarkdown: string; note: string }> = ({
  bodyMarkdown,
  note
}) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      {renderMarkdownToPdf(bodyMarkdown, pdfMarkdownStyles)}
      {note.trim() && (
        <View>
          <Text style={pdfStyles.noteBody}>{note}</Text>
        </View>
      )}
    </Page>
  </Document>
);

// The letter-paper card look shared by the Preview tab's read-only body.
const LetterPaper: React.FC<{ isLoading?: boolean; children: React.ReactNode }> = ({
  isLoading,
  children
}) => {
  if (isLoading) {
    return <TextWrapper text="Loading schedule data..." />;
  }
  return (
    <div
      id="print-schedule-content"
      style={{
        background: '#fff',
        minHeight: 400,
        padding: '16px 0',
        fontFamily: 'Lato, Arial, sans-serif',
        color: '#213147',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}>
      {children}
    </div>
  );
};

const PrintScheduleContent: React.FC<{
  isLoading?: boolean;
  bodyMarkdown: string;
  note: string;
}> = ({ isLoading, bodyMarkdown, note }) => (
  <LetterPaper isLoading={isLoading}>
    {bodyMarkdown.trim() ? (
      splitMarkdownIntoBlocks(bodyMarkdown).map((rawBlock, idx) => {
        const { align, text } = extractBlockAlignment(rawBlock);
        // An empty block represents an intentional blank line (see printMarkdown.ts) —
        // markdown-to-jsx renders nothing at all for an empty string, so give it explicit
        // height here rather than silently collapsing the spacing the org admin added.
        if (!text.trim()) {
          return <div key={idx} style={{ height: '1em' }} />;
        }
        return (
          <div key={idx} style={{ textAlign: align }}>
            <Markdown>{preserveLineBreaksForMarkdown(text)}</Markdown>
          </div>
        );
      })
    ) : (
      <TextWrapper size="sm" c="dimmed" text="Nothing to preview." />
    )}
    {note.trim() && <div style={{ marginTop: 20, whiteSpace: 'pre-wrap' }}>{note}</div>}
  </LetterPaper>
);

const PrintScheduleIndex: React.FC<PrintScheduleIndexProps> = (props) => {
  const { patient } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const patientName = `${patient?.givenName ?? ''} ${patient?.familyName ?? ''}`;

  const [updatePatient, { isLoading: isSaving }] = useUpdatePatientMutation();

  const scheduleData = (props.printData?.data ?? [])
    .filter((schedule) => appointmentTypeKey(schedule.type) === 'routine')
    .slice(0, 3);

  // The org must have a default letter configured (via operations-portal) before it can be
  // previewed/downloaded — the same letter is shown to every patient in the clinic, there is no
  // per-patient override or edit anymore.
  const orgReportDisplayText = patient?.clinic?.reportDisplayText ?? '';
  const isOrgConfigured = !!orgReportDisplayText;

  const savedNote = patient.patientPrintNote ?? '';
  const [note, setNote] = useState(savedNote);

  // Reset the draft note only on the actual closed->open transition (tracked via this ref), not
  // on every render where `savedNote` happens to change while the modal is already open — e.g. a
  // background refetch of `patient` would otherwise silently overwrite an in-progress edit.
  const wasModalOpen = useRef(false);
  useEffect(() => {
    if (props.modalOpen && !wasModalOpen.current) {
      setNote(savedNote);
    }
    wasModalOpen.current = props.modalOpen;
  }, [props.modalOpen, savedNote]);

  // ${nextScheduleDate} substitutes to the full list of the patient's upcoming routine schedule
  // dates (up to 3), not just the single next one — the org's print text must include
  // ${nextScheduleDate} itself to show it.
  const nextScheduleDate = scheduleData.length
    ? scheduleData
        .map((schedule) => formatDate(schedule.scheduledDate, 'dddd, MMMM D, YYYY'))
        .join('\n')
    : 'No upcoming scheduled dates.';

  // Patient's mailing address, formatted as a single readable line for use in ${patientAddress}.
  const patientAddress =
    [
      patient?.address?.line1,
      patient?.address?.line2,
      patient?.address?.city,
      patient?.address?.state,
      patient?.address?.postalCode
    ]
      .filter(Boolean)
      .join(', ') || 'No address on file';

  const vars: PrintTextVariables = {
    patientName,
    patientAddress,
    clinicName: patient?.clinic?.name ?? '',
    currentDate: formatDate(new Date(), 'dddd, MMMM D, YYYY'),
    nextScheduleDate
  };

  const substitutedBody = substitutePrintTextVariables(orgReportDisplayText, vars);
  const hasContent = !!substitutedBody.trim();
  const isNoteUnchanged = note === savedNote;

  const handleSaveNote = async () => {
    if (isNoteUnchanged || isSaving) return;
    try {
      await updatePatient({ patient: { id: patient.id, patientPrintNote: note } }).unwrap();
      showNotification({ title: 'Success', message: 'Note saved successfully', color: 'green' });
    } catch {
      showNotification({ message: 'Failed to save note. Please try again.', color: 'red' });
    }
  };

  const handleDownloadPDF = async () => {
    if (!hasContent) return;
    const formattedDate = formatDate(new Date(), 'dddd, MMMM D, YYYY');
    const blob = await pdf(
      <ScheduleLetterPdfDocument bodyMarkdown={substitutedBody} note={savedNote} />
    ).toBlob();

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formattedDate}_${patientName}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  if (!isOrgConfigured) {
    return (
      <CustomModal
        modalProps={{
          opened: props.modalOpen,
          onClose: () => props.setModalOpen(false),
          size: 'lg',
          closeOnClickOutside: false,
          closeOnEscape: false
        }}
        header={{
          title: 'Edit Schedule Letter'
        }}>
        <Flex align="center" justify="center" mih={360} p="xl">
          <InfoMessage
            title="Nothing has been set up for this organization"
            message="An admin needs to set up the print text for the schedule letter before it can be previewed or downloaded for patients."
            maw={420}
          />
        </Flex>
      </CustomModal>
    );
  }

  return (
    <CustomModal
      modalProps={{
        opened: props.modalOpen,
        onClose: () => props.setModalOpen(false),
        size: 'lg',
        closeOnClickOutside: false,
        closeOnEscape: false
      }}
      header={{
        title: 'Edit Schedule Letter'
      }}
      footer={
        <Flex p="md" justify="flex-end">
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadPDF}
            disabled={!hasContent}>
            Download
          </Button>
        </Flex>
      }>
      <div ref={contentRef}>
        <Box px="md">
          <PrintScheduleContent
            isLoading={props.isLoading}
            bodyMarkdown={substitutedBody}
            note={savedNote}
          />
        </Box>

        <Divider my="md" />

        <Box px="md" pb="md">
          <TextWrapper size="sm" fw={600} mb={6} text="Note" />
          <Flex gap="xs" align="flex-start">
            <Textarea
              style={{ flex: 1 }}
              placeholder="Add a note about this patient's letter or printing..."
              autosize
              minRows={2}
              maxRows={6}
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
              disabled={isSaving}
            />
            <Tooltip label="Save note">
              <ActionIcon
                variant={isNoteUnchanged ? 'default' : 'filled'}
                color="plum"
                size="lg"
                onClick={handleSaveNote}
                disabled={isNoteUnchanged}
                loading={isSaving}>
                <IconCheck size={16} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        </Box>
      </div>
    </CustomModal>
  );
};

export default PrintScheduleIndex;
